import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import useScrollLock from "../../hooks/useScrollLock";
import axios from "axios";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, getLoggedInUser} from "../../utils/utility";
import {convertUtcToLocal} from "../../utils/dateUtils";
import "../../styles/components/location-modal.css";
import {compassDirectionFromBearing, vehicleSpriteUrl} from "../../utils/vehicleIcons";
import {subscribeMobiTrack, isMobiTrackLive, getLatestMobiTrack} from "../../utils/mobiTrackRealtime";
import {GT06_STALE_THRESHOLD_MS, hasReliableGpsFix, isGt06PositionStale} from "../../utils/deviceStatus";

// Marker goes OFFLINE if no WS message arrives within this window. Previously a
// private 90s timeout here disagreed with the shared GT06_STALE_THRESHOLD_MS the
// device list/detail panel use, so the live map could flip a tracker OFFLINE
// minutes before the list did for the same device. Point it at the same shared
// threshold (deviceStatus.js) so every surface agrees.
const GT06_OFFLINE_TIMEOUT_MS = GT06_STALE_THRESHOLD_MS;
// Animation: min/max duration for one segment (ms)
const ANIM_MIN_MS = 2_000;
const ANIM_MAX_MS = 60_000;
// Maximum GPS frames to buffer (playback head lags behind real-time by this many frames)
const QUEUE_MAX = 5;

/** Ease-in-out cubic: 0→1 smoothly, with gentle acceleration and deceleration. */
const easeInOut = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

/** Shortest-path angle difference, accounting for the 0/360 wrap. */
const shortestAngleDiff = (from, to) => {
  let d = to - from;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
};

export default function LiveLocationModal({
  assetUnitId,
  latitude,
  longitude,
  onClose,
  assetType,
  assetName,
  deviceStatus,
  initialSpeedKmph,
  vehicleType,
}) {
  useScrollLock();

  const mapRef = useRef(null);
  const mapRefInstance = useRef(null);
  const markerRef = useRef(null);
  const vehicleOverlayRef = useRef(null); // OverlayView wrapping the segment <img>
  // Current compass-direction bucket shown by the vehicle sprite <img> — swap img.src only
  // when this changes (not on every draw/frame) to avoid needless reloads/flicker.
  const vehicleDirRef = useRef(null);
  const intervalRef = useRef(null);
  const stompClientRef = useRef(null);
  const mobiUnsubRef = useRef(null); // shared MobiTrack socket-store unsubscribe

  // requestAnimationFrame handle — replaces the old setInterval animation
  const animFrameRef = useRef(null);
  // Queue of pending GPS waypoints: { lat, lng, bearing, gpsTsMs }
  const posQueueRef = useRef([]);
  // True while animating a segment (prevents double-starting the loop)
  const isAnimatingRef = useRef(false);

  const isGt06 = assetType === "GT06_TRACKER";
  const isMobiTrack = assetType === "MOBI_TRACK";
  const statusLabel = isGt06 ? "GPS Tracker" : "Track Point";

  // Track Point too: open OFFLINE when the row/detail we came from is offline, so re-opening the
  // map for an off-duty driver doesn't flash "online" before the seed resolves.
  const initiallyOffline = (isGt06 || isMobiTrack) && deviceStatus !== Constants.DEVICE_ONLINE;

  // Current rendered position + heading (updated at the END of each animation segment)
  const currentPosRef = useRef({
    lat: latitude || 0,
    lng: longitude || 0,
    bearing: 0,
  });

  const lastWsMsgRef = useRef(
    initiallyOffline ? Date.now() - (GT06_OFFLINE_TIMEOUT_MS + 1000) : Date.now()
  );
  // Time of the last frame that carried a REAL, trusted GPS fix (hasReliableGpsFix) — separate
  // from lastWsMsgRef, which any frame (including a no-fix heartbeat) advances. Gates displayed
  // SPEED only, mirroring HomeCentralSection's isGt06PositionStale so the list, detail panel and
  // this map all stop trusting a stale speed at the same moment instead of the map showing a
  // frozen speed indefinitely while heartbeats keep the device ONLINE. Ignition/accOn is NOT
  // gated by this — see deviceStatus.js's isGt06PositionStale doc comment for why. Seeded to "now"
  // (not 0) so an initially-online device's seeded speed isn't immediately nulled before the
  // first live frame confirms it one way or the other.
  const lastFixMsRef = useRef(Date.now());
  const lastFetchesRef = useRef([]);

  const [isOffline, setIsOffline] = useState(initiallyOffline);
  const [offlineMessage, setOfflineMessage] = useState(
    initiallyOffline ? "Device is offline" : ""
  );
  const [mapType, setMapType] = useState("roadmap");
  const [lastUpdateTime, setLastUpdateTime] = useState("");
  const [status, setStatus] = useState(initiallyOffline ? "offline" : "online");
  const [speedKmph, setSpeedKmph] = useState(initialSpeedKmph ?? null);
  const [accOn, setAccOn] = useState(initiallyOffline ? false : null);

  const user = getLoggedInUser();
  const clientId = getClientIdOfUser();

  /* =========================
     INIT
  ========================= */

  useEffect(() => {
    if (!window.google || !window.google.maps) return;

    const initLat = latitude || 0;
    const initLng = longitude || 0;
    initMap(initLat, initLng);

    setTimeout(() => focusOnMarker(), 2000);

    if (isGt06) {
      startGt06Watching();
    } else if (isMobiTrack) {
      startMobiTrackWatching();
    } else {
      startWatching();
    }

    return () => {
      stopWatching();
      if (mobiUnsubRef.current) { mobiUnsubRef.current(); mobiUnsubRef.current = null; }
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (vehicleOverlayRef.current) vehicleOverlayRef.current.setMap(null);
      if (stompClientRef.current?.connected) stompClientRef.current.disconnect();
    };
    // eslint-disable-next-line
  }, []);

  /* =========================
     MAP
  ========================= */

  const initMap = (lat, lng) => {
    const mapOptions = {
      zoom: 16,
      center: { lat, lng },
      disableDefaultUI: true,
      gestureHandling: "greedy",
    };
    // Heading-up (course-up) live view: the vehicle always travels toward the top of the screen,
    // like car navigation. Map rotation (heading) ONLY works on a Google *vector* map, which
    // requires a Map ID. When one is configured we switch to a vector map and start it north-up
    // (flat, tilt 0); setMarkerPose then rotates the camera to the bearing. Without a Map ID the
    // map stays the classic north-up raster map — unchanged behaviour, zero regression.
    if (Constants.GOOGLE_MAPS_VECTOR_MAP_ID) {
      mapOptions.mapId = Constants.GOOGLE_MAPS_VECTOR_MAP_ID;
      mapOptions.heading = 0;
      mapOptions.tilt = 0;
    }
    const map = new window.google.maps.Map(mapRef.current, mapOptions);

    // Full-colour marker (nose up) when the asset declares a segment. google.maps Symbols are
    // monochrome — the truck rendered as two flat blobs — so the segment marker is a real
    // <img> inside an OverlayView, positioned every frame like the Android live map. Unlike the
    // old vector art, these are fixed-camera-angle 3D-rendered sprites (one per 8 compass
    // directions, see vehicleIcons.js) — heading is shown by swapping to the nearest-direction
    // sprite, never by CSS-rotating the image.
    const initialDir = compassDirectionFromBearing(0);
    const vehicleImgUrl = vehicleSpriteUrl(vehicleType, 0);
    if (vehicleImgUrl) {
      vehicleDirRef.current = initialDir;
      const img = document.createElement("img");
      img.src = vehicleImgUrl;
      img.alt = "";
      // A plain CSS `border` would draw a box around the image's full (mostly transparent)
      // bounding rect, which looks wrong on an irregular vehicle silhouette and would even
      // change shape between the narrow "N" sprite and the wide "E" one. Stacking 8
      // zero-blur drop-shadows instead traces an outline that hugs the actual non-transparent
      // pixels, consistently, at any direction/aspect ratio.
      img.style.cssText =
        "position:absolute;max-width:92px;max-height:92px;pointer-events:none;" +
        "filter:" +
        "drop-shadow(1.5px 0 0 #363166) drop-shadow(-1.5px 0 0 #363166) " +
        "drop-shadow(0 1.5px 0 #363166) drop-shadow(0 -1.5px 0 #363166) " +
        "drop-shadow(1.1px 1.1px 0 #363166) drop-shadow(-1.1px 1.1px 0 #363166) " +
        "drop-shadow(1.1px -1.1px 0 #363166) drop-shadow(-1.1px -1.1px 0 #363166) " +
        "drop-shadow(0 2px 3px rgba(15,23,42,0.45));" +
        "transform:translate(-50%,-50%);";

      const overlay = new window.google.maps.OverlayView();
      overlay.pose = { lat, lng, bearing: 0 };
      overlay.onAdd = function () {
        this.getPanes().overlayMouseTarget.appendChild(img);
      };
      overlay.draw = function () {
        const proj = this.getProjection();
        if (!proj) return;
        const px = proj.fromLatLngToDivPixel(
          new window.google.maps.LatLng(this.pose.lat, this.pose.lng)
        );
        img.style.left = px.x + "px";
        img.style.top = px.y + "px";
        const dir = compassDirectionFromBearing(this.pose.bearing);
        if (dir !== vehicleDirRef.current) {
          vehicleDirRef.current = dir;
          img.src = vehicleSpriteUrl(vehicleType, this.pose.bearing);
        }
      };
      overlay.onRemove = function () {
        img.remove();
      };
      overlay.setMap(map);
      vehicleOverlayRef.current = overlay;
    } else {
      const marker = new window.google.maps.Marker({
        map,
        position: { lat, lng },
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 7,
          fillColor: "#FF683A",
          fillOpacity: 1,
          strokeColor: "#363166",
          strokeWeight: 2.5,
          rotation: 0,
        },
        optimized: false,
      });
      markerRef.current = marker;
    }

    mapRefInstance.current = map;
    currentPosRef.current = { lat, lng, bearing: 0 };
  };

  /** Move whichever marker style is active (vehicle overlay or arrow). */
  const setMarkerPose = (lat, lng, bearing) => {
    const map = mapRefInstance.current;
    // Heading-up mode (vector Map ID configured): rotate the map so the direction of travel points
    // to the top of the screen, and keep the vehicle centred. The map itself rotates, so the
    // vehicle sprite always shows its "N" (nose-up) direction — exactly correct, since the camera
    // rotation is what makes it point the right way on screen. North-up mode: leave the camera
    // alone (user can pan freely) and pick the sprite for the true bearing instead.
    const headingUp = !!Constants.GOOGLE_MAPS_VECTOR_MAP_ID && !!map;
    if (headingUp) {
      map.moveCamera({ center: { lat, lng }, heading: bearing });
    }
    const renderBearing = headingUp ? 0 : bearing;
    const overlay = vehicleOverlayRef.current;
    if (overlay) {
      overlay.pose = { lat, lng, bearing: renderBearing };
      overlay.draw();
      return;
    }
    const marker = markerRef.current;
    if (marker) {
      marker.setPosition({ lat, lng });
      marker.setIcon({ ...marker.getIcon(), rotation: renderBearing });
    }
  };

  /* =========================
     SMOOTH ANIMATION ENGINE
     Uses requestAnimationFrame + a position queue so movement is
     continuous between GPS frames.  The queue holds up to QUEUE_MAX
     waypoints; the playback head always has a "next" target to move
     toward, eliminating the frozen-marker pause between updates.
  ========================= */

  /**
   * Push a new GPS fix into the queue.  Trims the oldest entry when
   * the queue exceeds QUEUE_MAX so real-time lag stays bounded.
   */
  const enqueuePosition = (lat, lng, bearing, gpsTsMs) => {
    const q = posQueueRef.current;
    q.push({ lat, lng, bearing, gpsTsMs });
    if (q.length > QUEUE_MAX) q.shift();
    if (!isAnimatingRef.current) animateNextSegment();
  };

  /**
   * Dequeue the front entry and animate toward the next one.
   * Duration is derived from the gap between the two GPS timestamps
   * (i.e. real vehicle travel time), clamped to [ANIM_MIN_MS, ANIM_MAX_MS].
   */
  const animateNextSegment = () => {
    const q = posQueueRef.current;
    if (q.length < 2) {
      isAnimatingRef.current = false;
      return;
    }

    isAnimatingRef.current = true;
    const from = q[0];
    const to = q[1];

    const fromLat = from.lat;
    const fromLng = from.lng;
    const fromBearing = from.bearing;
    const toLat = to.lat;
    const toLng = to.lng;
    const toBearing = to.bearing;

    const rawDuration = to.gpsTsMs - from.gpsTsMs;
    const durationMs = Math.max(ANIM_MIN_MS, Math.min(ANIM_MAX_MS, rawDuration || ANIM_MIN_MS));
    const bearingDiff = shortestAngleDiff(fromBearing, toBearing);

    const startWall = performance.now();

    const tick = (now) => {
      const elapsed = now - startWall;
      const t = Math.min(1, elapsed / durationMs);
      const e = easeInOut(t);

      const lat = fromLat + (toLat - fromLat) * e;
      const lng = fromLng + (toLng - fromLng) * e;
      const bearing = fromBearing + bearingDiff * e;

      setMarkerPose(lat, lng, bearing);

      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        // Segment complete — advance queue and continue
        const map = mapRefInstance.current;
        setMarkerPose(toLat, toLng, toBearing);
        // Heading-up mode already recentres via moveCamera inside setMarkerPose (which also
        // rotates the camera to the bearing); an unguarded panTo() right after fights that —
        // Maps' animated pan can clobber a heading just applied to a vector map. Northbound
        // travel (bearing ~0) hid this since resetting to ~0 looks unchanged, but southbound
        // travel (bearing ~180) snapped the map back to north-up on every segment. Mirrors the
        // Android live map, which already guards this the same way.
        const headingUp = !!Constants.GOOGLE_MAPS_VECTOR_MAP_ID && !!map;
        if (map && !headingUp) map.panTo({ lat: toLat, lng: toLng });
        currentPosRef.current = { lat: toLat, lng: toLng, bearing: toBearing };

        q.shift(); // remove the "from" entry; "to" becomes the new head
        animateNextSegment(); // recurse into next segment if available
      }
    };

    animFrameRef.current = requestAnimationFrame(tick);
  };

  /* =========================
     GT06 — WEBSOCKET MODE
  ========================= */

  const startGt06Watching = () => {
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const stompClient = Stomp.over(socket);
    stompClient.debug = null;
    stompClientRef.current = stompClient;

    stompClient.connect({}, () => {
      stompClient.subscribe("/topic/gpsData", (message) => {
        try {
          const data = JSON.parse(message.body);
          if (data.deviceId !== assetUnitId || data.assetType !== "GT06_TRACKER") return;

          lastWsMsgRef.current = Date.now();
          setIsOffline(false);
          setStatus("online");
          setOfflineMessage("");

          if (data.dateTime) setLastUpdateTime(convertUtcToLocal(data.dateTime));

          // GT06/FT920 frames always carry lat/lng, even a no-fix frame (0.0/0.0) — so
          // presence alone can't tell a real fix from none. hasReliableGpsFix trusts the
          // backend's own gpsFix flag instead (matches Android/HomeCentralSection).
          const hasFix = hasReliableGpsFix(data.latitude != null, data.longitude != null, data.gpsFix);
          if (hasFix) {
            lastFixMsRef.current = data.dateTime ? new Date(data.dateTime).getTime() : Date.now();
          }
          if (data.speed !== undefined && data.speed !== null) {
            // A heartbeat can keep the socket "online" indefinitely without a real fix —
            // don't let a long-stale speed reading sit on screen forever.
            setSpeedKmph(isGt06PositionStale(lastFixMsRef.current) ? null : data.speed);
          }
          if (data.accOn !== undefined && data.accOn !== null) setAccOn(data.accOn);

          if (!hasFix) return;

          const gpsTsMs = data.dateTime ? new Date(data.dateTime).getTime() : Date.now();
          const bearing = getBearing(
            currentPosRef.current.lat,
            currentPosRef.current.lng,
            data.latitude,
            data.longitude
          );

          // Use course reported by device if available (more accurate on straights)
          const finalBearing = data.course != null ? data.course : bearing;
          enqueuePosition(data.latitude, data.longitude, finalBearing, gpsTsMs);
        } catch (e) {
          console.error("LiveLocationModal WS parse error:", e);
        }
      });
    }, (err) => {
      console.error("LiveLocationModal WS connect error:", err);
      setIsOffline(true);
      setStatus("offline");
      setAccOn(false);
      setOfflineMessage("WebSocket connection failed");
    });

    // Periodically check if device has gone silent
    intervalRef.current = setInterval(() => {
      if (Date.now() - lastWsMsgRef.current > GT06_OFFLINE_TIMEOUT_MS) {
        setIsOffline(true);
        setStatus("offline");
        setAccOn(false);
        setOfflineMessage("No location update received");
      } else if (isGt06PositionStale(lastFixMsRef.current)) {
        // Still online (heartbeats keep arriving) but no real fix in a while — the last
        // known speed is no longer trustworthy, even though nothing forced OFFLINE.
        setSpeedKmph(null);
      }
    }, 30_000);
  };

  /* =========================
     MOBI_TRACK — WEBSOCKET MODE (shared realtime store)
  ========================= */

  // Position AND online/offline now come from the socket, not polling. The only
  // periodic REST call left is the lightweight "watched" ping, which tells the
  // driver app to keep posting at the high (5s) cadence while this map is open —
  // it carries no location data. One initial `latest` fetch seeds the first paint.
  const startMobiTrackWatching = () => {
    markAsWatched();
    fetchLatestLocation(); // one-time seed (first position + initial online/offline)

    mobiUnsubRef.current = subscribeMobiTrack(assetUnitId, (frame) => {
      if (!frame) return;
      // Explicit duty-off signal — flip the pill OFFLINE at once instead of waiting
      // for the silence timeout below.
      if (frame.offline) {
        setIsOffline(true);
        setStatus("offline");
        setOfflineMessage("Driver ended duty");
        return;
      }
      setIsOffline(false);
      setStatus("online");
      setOfflineMessage("");
      if (frame.timestamp) setLastUpdateTime(convertUtcToLocal(frame.timestamp));
      if (frame.latitude == null || frame.longitude == null) return;
      const gpsTsMs = frame.timestamp ? new Date(frame.timestamp).getTime() : Date.now();
      const bearing = getBearing(
        currentPosRef.current.lat, currentPosRef.current.lng, frame.latitude, frame.longitude
      );
      enqueuePosition(frame.latitude, frame.longitude, bearing, gpsTsMs);
    });

    // Keep the "watched" flag warm (driver posts at 5s while watched) and flip the
    // pill OFFLINE when the socket has been silent past the online window.
    intervalRef.current = setInterval(() => {
      markAsWatched();
      if (!isMobiTrackLive(assetUnitId)) {
        setIsOffline(true);
        setStatus("offline");
        setOfflineMessage("No location update received");
      }
    }, 15000);
  };

  /* =========================
     (legacy) MOBI_TRACK REST poll — kept for any non-GT06/non-MobiTrack caller
  ========================= */

  const startWatching = () => {
    markAsWatched();
    fetchLatestLocation();
    intervalRef.current = setInterval(() => {
      markAsWatched();
      fetchLatestLocation();
    }, 5000);
  };

  const stopWatching = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const markAsWatched = async () => {
    if (!assetUnitId) return;
    try {
      await axios.get(
        `${Constants.BASE_URL}api/auth/v1/mobi-track/watch/${clientId}/${assetUnitId}/${user.userId}`,
        { headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` } }
      );
    } catch (e) {
      console.error("markAsWatched failed:", e.message);
    }
  };

  function formatTimeAgo(diffMinutes) {
    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) { const m = Math.floor(diffMinutes); return `${m} minute${m !== 1 ? "s" : ""} ago`; }
    const diffHours = diffMinutes / 60;
    if (diffHours < 24) { const h = Math.floor(diffHours); return `${h} hour${h !== 1 ? "s" : ""} ago`; }
    const diffDays = diffHours / 24;
    if (diffDays < 30) { const d = Math.floor(diffDays); return `${d} day${d !== 1 ? "s" : ""} ago`; }
    const diffMonths = diffDays / 30;
    if (diffMonths < 12) { const mo = Math.floor(diffMonths); return `${mo} month${mo !== 1 ? "s" : ""} ago`; }
    const y = Math.floor(diffMonths / 12); return `${y} year${y !== 1 ? "s" : ""} ago`;
  }

  const fetchLatestLocation = async () => {
    if (!assetUnitId) return;
    try {
      const res = await axios.get(
        `${Constants.BASE_URL}api/auth/v1/mobi-track/latest/${assetUnitId}`,
        { headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` } }
      );
      const data = res.data;
      const newLat = data?.latitude;
      const newLng = data?.longitude;
      const timestamp = data?.timestamp;
      if (!newLat || !newLng || !timestamp) return;

      const locationKey = `${newLat}_${newLng}_${timestamp}`;
      lastFetchesRef.current.push(locationKey);
      if (lastFetchesRef.current.length > 5) lastFetchesRef.current.shift();

      if (lastFetchesRef.current.length === 5) {
        const allSame = lastFetchesRef.current.every((k) => k === lastFetchesRef.current[0]);
        if (allSame) {
          setIsOffline(true); setStatus("offline");
          setAccOn(false);
          setOfflineMessage("Location hasn't updated.");
          return;
        }
      }

      const locationTime = new Date(timestamp);
      const diffMinutes = (Date.now() - locationTime) / 60000;
      if (diffMinutes > 5) {
        setIsOffline(true); setStatus("offline");
        setAccOn(false);
        setOfflineMessage(`Last location update was ${formatTimeAgo(diffMinutes)}`);
        return;
      }

      // The driver may have ENDED DUTY: the server still returns their last location (which can be
      // < 5 min old), but it also carries a durable off-duty marker (`data.offline`, from
      // User.dutyEndedAt) that survives a page reload, plus the in-memory store's live duty-off
      // flag. Honour either so the marker still shows the last position but the pill stays OFFLINE
      // instead of flashing "online" on re-open or refresh after duty-off.
      const cached = getLatestMobiTrack(assetUnitId);
      if (data.offline === true || (cached && cached.offline)) {
        setIsOffline(true); setStatus("offline");
        setAccOn(false);
        setOfflineMessage("Driver is off duty");
        setLastUpdateTime(convertUtcToLocal(timestamp));
        const gpsTsMs = new Date(timestamp).getTime();
        const bearing = getBearing(
          currentPosRef.current.lat, currentPosRef.current.lng, newLat, newLng
        );
        enqueuePosition(newLat, newLng, bearing, gpsTsMs);
        return;
      }

      setIsOffline(false); setStatus("online"); setOfflineMessage("");
      setLastUpdateTime(convertUtcToLocal(timestamp));
      if (data?.speed !== undefined && data?.speed !== null) setSpeedKmph(data.speed);

      const gpsTsMs = new Date(timestamp).getTime();
      const bearing = getBearing(
        currentPosRef.current.lat, currentPosRef.current.lng, newLat, newLng
      );
      enqueuePosition(newLat, newLng, bearing, gpsTsMs);
    } catch (e) {
      console.error("Failed to fetch location:", e.message);
      setIsOffline(true); setStatus("offline");
      setOfflineMessage("Unable to fetch location data");
    }
  };

  /* =========================
     BEARING
  ========================= */

  const getBearing = (lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;
    const dLon = toRad(lon2 - lon1);
    const y = Math.sin(dLon) * Math.cos(toRad(lat2));
    const x =
      Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
      Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
    return ((toDeg(Math.atan2(y, x)) + 360) % 360);
  };

  /* =========================
     MAP CONTROLS
  ========================= */

  const focusOnMarker = () => {
    const map = mapRefInstance.current;
    if (!map) return;
    const { lat, lng } = currentPosRef.current;
    map.panTo({ lat, lng });
    map.setZoom(17);
  };

  const toggleMapType = () => {
    const map = mapRefInstance.current;
    if (!map) return;
    const newType = mapType === "roadmap" ? "satellite" : "roadmap";
    setMapType(newType);
    map.setMapTypeId(newType);
  };

  const zoomIn = () => {
    const map = mapRefInstance.current;
    if (map) map.setZoom(Math.min(map.getZoom() + 1, 21));
  };

  const zoomOut = () => {
    const map = mapRefInstance.current;
    if (map) map.setZoom(Math.max(map.getZoom() - 1, 1));
  };

  const handleClose = () => {
    stopWatching();
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (stompClientRef.current?.connected) stompClientRef.current.disconnect();
    onClose();
  };

  /* =========================
     UI
  ========================= */

  return createPortal(
    <div className="live-map-overlay">
      <div className="live-map-container">
        <button className="close-btn" onClick={handleClose}>✕</button>

        {/* Device identifier pill — top-centre of the map */}
        <div style={{
          position: "absolute",
          top: "12px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.72)",
          borderRadius: "20px",
          padding: "5px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 1001,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.88rem", lineHeight: 1.3 }}>
            {assetName || assetUnitId}
          </span>
          {assetName && (
            <span style={{ color: "#bbb", fontSize: "0.72rem", lineHeight: 1.2 }}>
              {assetUnitId}
            </span>
          )}
        </div>

        {/* Offline Alert */}
        {isOffline && (
          <div className="offline-alert">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="offline-content">
              <strong>{statusLabel} Offline </strong>
              <span>{offlineMessage}</span>
            </div>
          </div>
        )}

        {status === "online" && (
          <div className="online-alert">
            <span className="online-dot"></span>
            <div className="online-content">
              <strong>{statusLabel} Online</strong>
            </div>
            {speedKmph != null && (
              <div className="live-speed-badge" title="Real-time speed">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm4.24 6.34l-3.18 5.66a1.5 1.5 0 11-2.12-2.12l5.66-3.18a.25.25 0 01.34.34z"/>
                </svg>
                <span className="live-speed-value">{Math.round(speedKmph)}</span>
                <span className="live-speed-unit">km/h</span>
                {isGt06 && accOn != null && (
                  <span className={`live-acc-pill ${accOn ? "on" : "off"}`}>
                    {accOn ? "IGN ON" : "IGN OFF"}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div ref={mapRef} className="map-container" />

        {/* Map Controls */}
        <div className="map-controls">
          <div className="zoom-controls">
            <button className="control-btn zoom-btn" onClick={zoomIn} title="Zoom in">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
            </button>
            <div className="zoom-divider"></div>
            <button className="control-btn zoom-btn" onClick={zoomOut} title="Zoom out">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z" />
              </svg>
            </button>
          </div>

          <button
            className="control-btn map-type-btn"
            onClick={toggleMapType}
            title={mapType === "roadmap" ? "Switch to Satellite" : "Switch to Map"}
          >
            {mapType === "roadmap" ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" />
              </svg>
            )}
          </button>
        </div>

        {/* FAB: Focus on location */}
        <button className="focus-fab" onClick={focusOnMarker} title="Focus on location">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}
